import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal, Button, Form } from 'react-bootstrap';
import { fifoService } from '../../services/fifoService';
import { toast } from 'react-toastify';
import PageLoader from '../../components/PageLoader';

const statusBadge = (status) => {
    if (status === 'Pass QC') return { badgeClass: 'bg-success', icon: 'bi-check-circle', label: 'ผ่าน' };
    if (status === 'Fail QC') return { badgeClass: 'bg-danger', icon: 'bi-x-circle', label: 'ไม่ผ่าน' };
    return { badgeClass: 'bg-info text-dark', icon: 'bi-hourglass-split', label: 'รอ QC' };
};

const QcRequestDetail = () => {
    const { document_id } = useParams();
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionTarget, setActionTarget] = useState(null); // { sn_number, status }
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchDetail = async () => {
        setLoading(true);
        try {
            const data = await fifoService.getQcRequestDetail(document_id);
            setDetail(data);
        } catch (error) {
            toast.error('ไม่พบเอกสารนี้');
            navigate('/qc-queue');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [document_id]);

    const openActionModal = (sn_number, status) => {
        setActionTarget({ sn_number, status });
        setReason('');
    };

    const closeActionModal = () => {
        setActionTarget(null);
        setReason('');
    };

    const submitAction = async () => {
        if (!actionTarget) return;
        if (actionTarget.status === 'Fail QC' && !reason.trim()) {
            toast.warning('กรุณาระบุสาเหตุที่ไม่ผ่าน');
            return;
        }

        setSaving(true);
        try {
            await fifoService.updateQcStatus(actionTarget.sn_number, actionTarget.status, reason.trim());
            toast.success(`บันทึกผล ${actionTarget.status === 'Pass QC' ? 'ผ่าน' : 'ไม่ผ่าน'} แล้ว`);
            closeActionModal();
            fetchDetail();
        } catch (error) {
            toast.error('เกิดข้อผิดพลาดในการบันทึก');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <PageLoader message="กำลังโหลดข้อมูล..." />;
    }

    if (!detail) {
        return null;
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <button className="btn btn-link text-muted p-0 mb-2" onClick={() => navigate('/qc-queue')}>
                        <i className="bi bi-arrow-left me-2"></i>
                        กลับไปยังรายการตรวจสอบ
                    </button>
                    <h3 className="mb-2">
                        <i className="bi bi-shield-check me-2 text-primary"></i>
                        {detail.document_id}
                    </h3>
                    <p className="text-muted mb-0">
                        {detail.createdAt && new Date(detail.createdAt).toLocaleDateString('th-TH', {
                            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                    </p>
                </div>
            </div>

            <div className="row">
                <div className="col-md-6 mb-4">
                    <div className="theme-card h-100">
                        <div className="theme-card-header">
                            <h5 className="mb-0"><i className="bi bi-person-fill me-2"></i>ข้อมูลลูกค้า</h5>
                        </div>
                        <div className="theme-card-body">
                            <div className="mb-3">
                                <label className="text-muted small">ชื่อลูกค้า</label>
                                <div className="fw-medium">{detail.customerName || '-'}</div>
                            </div>
                            <div className="mb-3">
                                <label className="text-muted small">ที่อยู่</label>
                                <div className="fw-medium">{detail.customerAddress || '-'}</div>
                            </div>
                            <div className="row">
                                <div className="col-6">
                                    <label className="text-muted small">วันที่ต้องการใช้เครื่อง</label>
                                    <div className="fw-medium">
                                        {detail.wantDate ? new Date(detail.wantDate).toLocaleDateString('th-TH') : '-'}
                                    </div>
                                </div>
                                <div className="col-6">
                                    <label className="text-muted small">ต้องการรับจาก QCM วันที่</label>
                                    <div className="fw-medium">
                                        {detail.qcmDate ? new Date(detail.qcmDate).toLocaleDateString('th-TH') : '-'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 mb-4">
                    <div className="theme-card h-100">
                        <div className="theme-card-header">
                            <h5 className="mb-0"><i className="bi bi-list-check me-2"></i>รายละเอียดการแจ้งงาน</h5>
                        </div>
                        <div className="theme-card-body">
                            {detail.requestDetails && detail.requestDetails.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                    {detail.requestDetails.map((job, idx) => (
                                        <li key={idx} className="mb-2">
                                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                                            {job}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-muted mb-0">ไม่มีรายการงาน</p>
                            )}

                            {detail.remark && (
                                <div className="mt-3 pt-3 border-top">
                                    <label className="text-muted small">หมายเหตุ</label>
                                    <div className="fw-medium">{detail.remark}</div>
                                </div>
                            )}

                            <div className="mt-3 pt-3 border-top">
                                <label className="text-muted small">ผู้แจ้งงาน</label>
                                <div className="fw-medium">{detail.requesterName || '-'} {detail.department ? `(${detail.department})` : ''}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ส่วนที่ 2: line items grouped by product, serials underneath, pass/fail per serial */}
            {detail.items.map((item, idx) => (
                <div className="theme-card mb-4" key={idx}>
                    <div className="theme-card-header">
                        <h5 className="mb-0">
                            <i className="bi bi-box-seam me-2"></i>
                            {item.productId || '-'} {item.description ? `— ${item.description}` : ''}
                        </h5>
                    </div>
                    <div className="theme-card-body p-0">
                        <div className="table-responsive">
                            <table className="table theme-table mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th style={{ width: '30%' }}>Serial Number</th>
                                        <th style={{ width: '20%' }}>สถานะ</th>
                                        <th style={{ width: '30%' }}>หมายเหตุ QC</th>
                                        <th style={{ width: '20%' }} className="text-end">ดำเนินการ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {item.serials.map((s, sIdx) => {
                                        const badge = statusBadge(s.status);
                                        const decided = s.status === 'Pass QC' || s.status === 'Fail QC';
                                        return (
                                            <tr key={sIdx}>
                                                <td className="fw-medium">
                                                    <i className="bi bi-upc-scan me-2 text-primary"></i>
                                                    {s.sn_number}
                                                </td>
                                                <td>
                                                    <span className={`badge ${badge.badgeClass}`}>
                                                        <i className={`bi ${badge.icon} me-1`}></i>
                                                        {badge.label}
                                                    </span>
                                                </td>
                                                <td className="text-muted">
                                                    {s.qcmRemark || '-'}
                                                    {s.qcmName && <div className="small">โดย {s.qcmName}</div>}
                                                </td>
                                                <td className="text-end">
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => openActionModal(s.sn_number, 'Pass QC')}
                                                        >
                                                            <i className="bi bi-check-lg me-1"></i> ผ่าน
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => openActionModal(s.sn_number, 'Fail QC')}
                                                        >
                                                            <i className="bi bi-x-lg me-1"></i> ไม่ผ่าน
                                                        </button>
                                                    </div>
                                                    {decided && (
                                                        <div className="small text-muted mt-1">แก้ไขผลได้หากจำเป็น</div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ))}

            <Modal show={!!actionTarget} onHide={closeActionModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {actionTarget?.status === 'Pass QC' ? 'บันทึกผล: ผ่าน QC' : 'บันทึกผล: ไม่ผ่าน QC'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-2">Serial: <span className="fw-medium">{actionTarget?.sn_number}</span></p>
                    <Form.Group>
                        <Form.Label>
                            {actionTarget?.status === 'Pass QC' ? 'หมายเหตุ (ถ้ามี)' : 'สาเหตุที่ไม่ผ่าน'}
                            {actionTarget?.status === 'Fail QC' && <span className="text-danger"> *</span>}
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder={actionTarget?.status === 'Pass QC' ? 'ระบุหมายเหตุ (ถ้ามี)' : 'ระบุสาเหตุที่ไม่ผ่าน'}
                            autoFocus
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeActionModal} disabled={saving}>ยกเลิก</Button>
                    <Button
                        variant={actionTarget?.status === 'Pass QC' ? 'success' : 'danger'}
                        onClick={submitAction}
                        disabled={saving}
                    >
                        {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default QcRequestDetail;
